/* eslint-disable ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import {
  uniq,
  without,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';

const PostGroupLearnersReducer = (learnerIds, post) => {
  if (!isEmpty(post.groups)) {
    // resolve the async hasMany relationship upfront.
    learnerIds = resolved(post.groups).reduce((learnerIds, group) => {
      learnerIds.push(...group.currentLearners.map((item) => item.personId));
      return learnerIds;
    }, learnerIds);
  }
  return learnerIds;
};

export default class ResultsController extends Controller {
  // Services
  @service
  store;

  @service
  strings;

  @computed('model.{communityNotices.@each.groups,studentNotices.@each.groups}')
  get allLearnerIds() {
    let learnerIds = [];

    // Push all learner IDs from all groups on surveyed community and student notices into the array.
    learnerIds = this.model.communityNotices.reduce(
      PostGroupLearnersReducer,
      learnerIds,
    );
    learnerIds = this.model.studentNotices.reduce(
      PostGroupLearnersReducer,
      learnerIds,
    );

    // Return a deduplicated list, since some learners may be in multiple groups or on multiple posts.
    return uniq(learnerIds);
  }

  @computed('allLearnerIds.[]')
  get allLearners() {
    let learners = [];
    this.allLearnerIds.forEach((id) => {
      learners.push(this.store.peekRecord('person', id));
    });

    return learners;
  }

  @computed('model.responses.@each.answer')
  get summary() {
    return this.model.responses.reduce((summary, response) => {
      let row = summary.find((item) => item.answer === response.answer);
      if (isEmpty(row)) {
        summary.push({ answer: response.answer, count: 0 });
        row = summary.find((item) => item.answer === response.answer);
      }
      row.count++;
      return summary;
    }, []);
  }

  @computed(
    'model.{responses.[],survey.answers.[]}',
    'selectedLearners.@each.id',
  )
  get answersWithAllSelected() {
    let allSelected = [...this.model.survey.answers];
    this.model.responses.forEach((response) => {
      if (allSelected.includes(response.answer)) {
        if (!this.selectedLearners.includes(response.person)) {
          allSelected = without(allSelected, response.answer);
        }
      }
    });
    return allSelected;
  }

  @alias('learnersWithoutResponses.length')
  numNotResponded;

  @computed('allLearners.[]', 'model.responses.[]')
  get learnersWithoutResponses() {
    return this.allLearners.filter((learner) => {
      return !this.model.responses.find(
        (item) => item.person_id === learner.id,
      );
    });
  }

  @computed('learnersWithoutResponses.[]', 'selectedLearners.[]')
  get allNotRespondedSelected() {
    return this.learnersWithoutResponses.every((learner) => {
      return this.selectedLearners.includes(learner);
    });
  }

  @tracked
  selectedLearners = [];

  @action
  allLearnersSelected(responses) {
    return responses.every((response) => {
      return this.selectedLearners.includes(response.person);
    });
  }

  @action
  toggleLearnersByResponse(responses, checked) {
    let selected = this.selectedLearners;
    responses.forEach((response) => {
      if (checked) {
        if (!selected.includes(response.person)) {
          selected = [...selected, response.person];
        }
      } else {
        selected = selected.filter((learner) => learner !== response.person);
      }
    });
    this.selectedLearners = selected;
  }

  @action
  toggleLearners(learners, checked) {
    let selected = this.selectedLearners;
    learners.forEach((learner) => {
      if (checked) {
        if (!selected.includes(learner)) {
          selected = [...selected, learner];
        }
      } else {
        selected = selected.filter((item) => item !== learner);
      }
    });
    this.selectedLearners = selected;
  }

  @action
  toggleLearner(learner, checked) {
    if (checked) {
      this.selectedLearners = [...this.selectedLearners, learner];
    } else {
      this.selectedLearners = this.selectedLearners.filter(
        (item) => item !== learner,
      );
    }
  }

  reset() {
    this.selectedLearners = [];
  }
}
